import { z } from 'zod'

/**
 * Usage of the public API (clipbook-api), CLI-1041: the alert rules Clipbook sets per client, what the
 * alert tick sent, and the usage series the API page charts. Counts are clipbook-api's (its daily rollup,
 * read through `GET /internal/usage`); what a billable "call" is belongs to that service alone.
 *
 * Alerts never block a call: clipbook-api's `monthlyQuota` is the only enforcement. Every number here is
 * a setting because what clients were promised differs (OpenAI: notice within 10M and 2M of a 60M-call
 * contract year, and "5%, 10%, 20% of your quota").
 */

/** clipbook-api reads at most this many days at once (its MAX_RANGE_DAYS). */
export const API_USAGE_MAX_RANGE_DAYS = 400

export const API_USAGE_MEASURES = ['billable', 'requests'] as const
export type ApiUsageMeasure = (typeof API_USAGE_MEASURES)[number]

/** 'day' and 'month' are UTC calendar periods; 'term' runs from termStartsOn up to (not including) termEndsOn. */
export const API_USAGE_PERIODS = ['day', 'month', 'term'] as const
export type ApiUsagePeriod = (typeof API_USAGE_PERIODS)[number]

const percent = z.number().gt(0).max(100)

/** A share of the limit used, a share left, or a number of calls left. */
export const usageAlertThresholdSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('used_percent'), value: percent }),
  z.object({ kind: z.literal('remaining_percent'), value: percent }),
  z.object({ kind: z.literal('remaining_calls'), value: z.number().int().positive() }),
])
export type UsageAlertThreshold = z.infer<typeof usageAlertThresholdSchema>

/** The ledger's name for a threshold: one per kind and value, e.g. `remaining_calls:10000000`. */
export function usageThresholdKey(threshold: UsageAlertThreshold): string {
  return `${threshold.kind}:${threshold.value}`
}

const MS_PER_DAY = 86_400_000

function daysBetween(from: string, to: string): number {
  return (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / MS_PER_DAY
}

const ruleFields = {
  name: z.string().trim().min(1).max(200),
  measure: z.enum(API_USAGE_MEASURES),
  period: z.enum(API_USAGE_PERIODS),
  termStartsOn: z.iso.date().nullable(),
  termEndsOn: z.iso.date().nullable(),
  callLimit: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
  thresholds: z.array(usageAlertThresholdSchema).min(1).max(20),
  recipients: z.array(z.email()).max(50),
  bcc: z.array(z.email()).max(50),
  enabled: z.boolean(),
}

type RuleShape = {
  period?: ApiUsagePeriod | undefined
  termStartsOn?: string | null | undefined
  termEndsOn?: string | null | undefined
  callLimit?: number | undefined
  thresholds?: UsageAlertThreshold[] | undefined
  recipients?: string[] | undefined
  bcc?: string[] | undefined
}

/** Thresholds the limit makes meaningless: as many calls left as the limit or more is crossed at zero used. */
function refuseMeaninglessThresholds(rule: RuleShape, ctx: z.RefinementCtx): void {
  const limit = rule.callLimit
  if (limit === undefined) return
  for (const threshold of rule.thresholds ?? []) {
    if (threshold.kind === 'remaining_calls' && threshold.value >= limit) {
      ctx.addIssue({
        code: 'custom',
        path: ['thresholds'],
        message: `"${threshold.value} calls left" must be fewer than the limit (${limit}).`,
      })
    }
  }
}

/** The rule's own consistency: a term has both dates, in order, within what clipbook-api reads; keys unique. */
function refuseInconsistentRule(rule: RuleShape, ctx: z.RefinementCtx): void {
  const hasTerm = Boolean(rule.termStartsOn) && Boolean(rule.termEndsOn)
  if (rule.period !== undefined && (rule.period === 'term') !== hasTerm) {
    ctx.addIssue({
      code: 'custom',
      path: ['period'],
      message: 'A term rule needs termStartsOn and termEndsOn; a day or month rule takes neither.',
    })
  }
  if (rule.termStartsOn && rule.termEndsOn) {
    const days = daysBetween(rule.termStartsOn, rule.termEndsOn)
    if (days < 1 || days > API_USAGE_MAX_RANGE_DAYS) {
      ctx.addIssue({
        code: 'custom',
        path: ['termEndsOn'],
        message: `termEndsOn must be after termStartsOn, at most ${API_USAGE_MAX_RANGE_DAYS} days later.`,
      })
    }
  }
  const keys = (rule.thresholds ?? []).map(usageThresholdKey)
  if (new Set(keys).size !== keys.length) {
    ctx.addIssue({ code: 'custom', path: ['thresholds'], message: 'Each threshold once.' })
  }
  refuseMeaninglessThresholds(rule, ctx)
  if (
    rule.recipients !== undefined &&
    rule.bcc !== undefined &&
    rule.recipients.length + rule.bcc.length === 0
  ) {
    ctx.addIssue({
      code: 'custom',
      path: ['recipients'],
      message: 'A rule needs someone to tell: at least one recipient or bcc.',
    })
  }
}

export const createUsageAlertRuleSchema = z
  .object({
    endpointId: z.uuid(),
    name: ruleFields.name,
    measure: ruleFields.measure.default('billable'),
    period: ruleFields.period,
    termStartsOn: ruleFields.termStartsOn.default(null),
    termEndsOn: ruleFields.termEndsOn.default(null),
    callLimit: ruleFields.callLimit,
    thresholds: ruleFields.thresholds,
    recipients: ruleFields.recipients.default([]),
    bcc: ruleFields.bcc.default([]),
    enabled: ruleFields.enabled.default(true),
  })
  .superRefine(refuseInconsistentRule)
export type CreateUsageAlertRule = z.infer<typeof createUsageAlertRuleSchema>

/**
 * A partial change. A change to `period` or the term dates must name all three (the route re-checks the
 * merged rule, since the stored row decides what a partial change means).
 */
export const updateUsageAlertRuleSchema = z
  .object(ruleFields)
  .partial()
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Nothing to change' })
export type UpdateUsageAlertRule = z.infer<typeof updateUsageAlertRuleSchema>

/** A whole rule after a patch, checked the way a create is. */
export const usageAlertRuleShapeSchema = z
  .object({
    period: ruleFields.period,
    termStartsOn: ruleFields.termStartsOn,
    termEndsOn: ruleFields.termEndsOn,
    callLimit: ruleFields.callLimit,
    thresholds: ruleFields.thresholds,
    recipients: ruleFields.recipients,
    bcc: ruleFields.bcc,
  })
  .superRefine(refuseInconsistentRule)

export const usageAlertRuleSchema = z.object({
  id: z.uuid(),
  endpointId: z.uuid(),
  name: z.string(),
  measure: z.enum(API_USAGE_MEASURES),
  period: z.enum(API_USAGE_PERIODS),
  termStartsOn: z.iso.date().nullable(),
  termEndsOn: z.iso.date().nullable(),
  callLimit: z.number().int(),
  thresholds: z.array(usageAlertThresholdSchema),
  recipients: z.array(z.string()),
  bcc: z.array(z.string()),
  enabled: z.boolean(),
  createdBy: z.uuid().nullable(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
})
export type UsageAlertRule = z.infer<typeof usageAlertRuleSchema>

export const usageAlertSendSchema = z.object({
  id: z.uuid(),
  ruleId: z.uuid(),
  windowStart: z.iso.date(),
  thresholdKey: z.string(),
  claimedAt: z.iso.datetime(),
  sentAt: z.iso.datetime().nullable(),
  outcome: z.enum(['sent', 'suppressed']).nullable(),
  used: z.number().int(),
  callLimit: z.number().int(),
  recipientCount: z.number().int().nullable(),
})
export type UsageAlertSend = z.infer<typeof usageAlertSendSchema>

/** A rule judged now without sending: what the next tick would do. */
export const usageAlertPreviewSchema = z.object({
  ruleId: z.uuid(),
  /** Null while a term rule has not begun or has ended: the tick skips it. */
  window: z.object({ start: z.iso.date(), end: z.iso.date() }).nullable(),
  used: z.number().int(),
  callLimit: z.number().int(),
  remaining: z.number().int(),
  /** Every threshold crossed in this window, most severe first. */
  crossed: z.array(z.string()),
  /** Crossed and not yet sent in this window: what the next tick emails. */
  pending: z.array(z.string()),
  subject: z.string().nullable(),
})
export type UsageAlertPreview = z.infer<typeof usageAlertPreviewSchema>

/**
 * A test send of a rule's alert (CLI-1041): to Clipbook addresses only (the route refuses any other),
 * never claimed, never on the ledger.
 */
export const usageAlertTestSendSchema = z.object({ to: z.array(z.email()).min(1).max(10) })
export type UsageAlertTestSend = z.infer<typeof usageAlertTestSendSchema>

/** A usage range: UTC days, `to` exclusive. */
export const apiUsageRangeQuerySchema = z.object({ from: z.iso.date(), to: z.iso.date() }).refine(
  (range) => {
    const days = daysBetween(range.from, range.to)
    return days >= 1 && days <= API_USAGE_MAX_RANGE_DAYS
  },
  { message: `to must be after from, at most ${API_USAGE_MAX_RANGE_DAYS} days later` },
)
export type ApiUsageRangeQuery = z.infer<typeof apiUsageRangeQuerySchema>

/** clipbook-api's answer to `GET /internal/usage`, parsed at the boundary. */
export const clipbookApiUsageResponseSchema = z.object({
  endpoint_id: z.uuid(),
  from: z.iso.date(),
  to: z.iso.date(),
  totals: z.object({
    requests: z.number().int().nonnegative(),
    billable_requests: z.number().int().nonnegative(),
    error_requests: z.number().int().nonnegative(),
  }),
  days: z.array(
    z.object({
      day: z.iso.date(),
      key_id: z.uuid(),
      /** The key is Clipbook's own (`api_keys.internal`): its calls are not the client's usage. */
      internal: z.boolean(),
      requests: z.number().int().nonnegative(),
      billable_requests: z.number().int().nonnegative(),
      error_requests: z.number().int().nonnegative(),
    }),
  ),
})
export type ClipbookApiUsageResponse = z.infer<typeof clipbookApiUsageResponseSchema>

/**
 * An endpoint's usage per UTC day and key, for the API page (Andre's usage chart, CLI-1036). `errors`
 * counts every response >= 400, the client's own 401s and 429s included: not an uptime figure. The
 * workspace's view carries the client's keys only, totalled over them; Clipbook's view every key, its
 * internal ones (Clipbook's own, never billed) flagged, and clipbook-api's totals over all of them.
 */
export const apiUsageSeriesSchema = z.object({
  endpointId: z.uuid(),
  from: z.iso.date(),
  to: z.iso.date(),
  /** When Headline read it; clipbook-api's counts lag requests by a few seconds. */
  asOf: z.iso.datetime(),
  totals: z.object({
    requests: z.number().int(),
    billable: z.number().int(),
    errors: z.number().int(),
  }),
  days: z.array(
    z.object({
      day: z.iso.date(),
      keyId: z.uuid(),
      /** Clipbook's own key's calls: only Clipbook's view of the series carries them. */
      internal: z.boolean(),
      requests: z.number().int(),
      billable: z.number().int(),
      errors: z.number().int(),
    }),
  ),
  /** The endpoint's keys by name, so a chart can label a key id (revoked keys included). */
  keys: z.array(
    z.object({ id: z.uuid(), name: z.string(), prefix: z.string(), internal: z.boolean() }),
  ),
})
export type ApiUsageSeries = z.infer<typeof apiUsageSeriesSchema>
